import { Capacitor } from '@capacitor/core';
import { coreApi } from './coreApi';
import { tenantApi } from './tenantApi';
import { unwrap } from './unwrap';
import { useAuthStore } from '../stores/authStore';
import { useBrandingStore } from '../stores/brandingStore';
import { useModuleStore } from '../stores/moduleStore';
import { useExperienceStore } from '../stores/experienceStore';
import { persistDomainResolutionSsoFlags } from '../lib/ssoRootScope';
import { env } from '@config/env';
import { readRuntimeDevConfig } from '../lib/localDevBootstrap';
import type { DomainResolutionResponse } from '../types/auth';
import type { DomainConfigData } from '../types/domainConfig';

/**
 * Cross-cutting endpoints that don't fit a single feature folder:
 *   - `POST /resolve-domain`  on Core API
 *   - `GET  /domain-config`   on Tenant API
 *
 * The functions below are pure data calls; their callers (bootstrap, auth
 * mutations, tenant switch) decide how to apply branding overlays and
 * persist SSO flags. Side effects on the relevant Zustand stores are
 * deliberate — these flows existed in Angular as service-internal `tap()`
 * operators and the React port keeps that responsibility centralized.
 */

interface ResolveDomainOptions {
  /**
   * When false, skip persisting `is_root_tenant` / `root_tenant_id`. Set
   * during signup-flow refreshes so the hub SSO scope isn't overwritten by
   * a child community's resolution.
   */
  persistSsoFlags?: boolean;
}

export async function resolveDomain(
  domain: string,
  options: ResolveDomainOptions = {},
): Promise<DomainResolutionResponse> {
  const persistSso = options.persistSsoFlags !== false;
  const domainToResolve = domainForResolution(domain);

  const { data } = await coreApi.post<DomainResolutionResponse>('/resolve-domain', {
    domain: domainToResolve,
  });

  const auth = useAuthStore.getState();
  auth.setRegionalApiUrl(data.regional_api_base_url);
  auth.setTenantId(data.tenant_id);

  if (persistSso) {
    persistDomainResolutionSsoFlags(data);
  }

  const branding = useBrandingStore.getState();
  const domainNorm = (domainToResolve || '').trim();
  const defaultNorm = effectiveDefaultDomain();

  const isHubVertical =
    (defaultNorm && domainNorm === defaultNorm) ||
    (!defaultNorm && data.is_root_tenant === true);

  if (isHubVertical && data.branding) {
    branding.setBaseBrandingFromResolution(
      domainNorm,
      data.branding.name,
      data.branding.primary_color,
      data.branding,
    );
  }

  branding.setBranding(
    domainNorm,
    data.branding?.name,
    data.branding?.primary_color,
    data.branding,
  );

  return data;
}

/**
 * True when the host environment cannot itself name the tenant domain, so we
 * should fall back to the active portal domain / env default instead of the
 * window hostname:
 *   - localhost (dev): the host is `localhost`, never a tenant.
 *   - native (Capacitor): the app is served from `capacitor://<shellHost>`,
 *     where `<shellHost>` is the `server.hostname` baked into the wrapper
 *     (= the env `portalBaseUrl` host, e.g. `ip4cms-member-za.ip4x.systems`).
 *     For multi-community zones (`defaultDomain: ''`) that shell host is NOT a
 *     resolvable tenant — resolving it mis-brands the app instead of routing to
 *     the community-select flow. We only treat the *shell host itself* this
 *     way; an explicitly-passed community domain still resolves normally.
 */
export function prefersConfiguredDomain(hostname: string): boolean {
  const isLocalhost =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('localhost:') ||
    hostname.startsWith('127.0.0.1:');

  const isNativeShellHost =
    Capacitor.isNativePlatform() &&
    typeof window !== 'undefined' &&
    hostname === window.location.hostname;

  return isLocalhost || isNativeShellHost;
}

/**
 * Effective default portal domain for localhost / native shell boot.
 * `public/runtime-env.js` (`window.__IP4X_DEV__`) wins over the Vite env file
 * when runtime-env pins a default domain for localhost.
 */
export function effectiveDefaultDomain(): string {
  const runtime = readRuntimeDevConfig()?.defaultDomain?.trim();
  if (runtime) return runtime;
  return (env.defaultDomain || '').trim();
}

/**
 * Decide the domain string to send to `/resolve-domain`. On localhost (or in a
 * native shell) we prefer the active portal domain (last login / tenant switch)
 * so refreshes don't re-resolve the SSO hub and clobber `paas_tenant_id`, and
 * so multi-community member zones land on community-select rather than a
 * mis-resolved shell host.
 */
export function domainForResolution(hostname: string): string {
  if (prefersConfiguredDomain(hostname)) {
    const pinned = effectiveDefaultDomain();
    try {
      const active = localStorage.getItem('paas_active_portal_domain')?.trim();
      const activeCommunityTenantId = localStorage.getItem('paas_active_community_tenant_id')?.trim();
      if (active) {
        // On a native install the active portal domain is the resident's selected
        // community (one build per install — no cross-vertical staleness), so always
        // trust it. Otherwise every app reopen re-resolves the configured hub domain
        // and clobbers the selected community's branding + tenant id (SSO reverts to
        // the hub).
        if (Capacitor.isNativePlatform()) return active;
        // Localhost core SSO: once the user has selected or switched into a
        // community, persist that community domain and keep resolving against it
        // across refreshes until the user changes communities again.
        if (activeCommunityTenantId) return active;
        // Localhost dev: a stale active domain may linger from another build/vertical;
        // only reuse it when it matches the configured pin.
        if (!pinned || active === pinned) return active;
        localStorage.removeItem('paas_active_portal_domain');
      }
    } catch { /* noop */ }
    return pinned;
  }
  return hostname;
}

/**
 * Revert domain resolution + branding to the SSO hub. Called when a resident
 * backs out of a community selection during the sign-in / sign-up journey —
 * without this the screen keeps the half-selected community's branding.
 * Mirrors the Angular signup `resetSignupToHubContext()`.
 */
export async function resetToHubContext(): Promise<void> {
  // Instant visual revert so colors don't linger while the hub re-resolves.
  useBrandingStore.getState().revertToBaseBranding();
  useModuleStore.getState().setConfig(null);
  useExperienceStore.getState().setExperience(null);
  try {
    localStorage.removeItem('paas_active_portal_domain');
  } catch {
    /* storage unavailable */
  }

  const hubDomain =
    effectiveDefaultDomain() ||
    (typeof window !== 'undefined' ? window.location.hostname : '');
  if (!hubDomain) return;

  try {
    await resolveDomain(hubDomain);
  } catch (error) {
    console.warn('[domain] reset to hub context failed:', error);
  }
}

/**
 * Tenant API `GET /public/domain-config`. Returns the per-tenant domain
 * configuration:
 *   - module enablement flags + per-module settings (incl. portal auth
 *     background imagery under `moduleConfig.portal.*`)
 *   - allowed unauthenticated functions
 *   - API key prefix
 *   - compiled app experience (tabs, tiles, banners)
 *
 * Response is `{ success, data }` enveloped. Side effect: writes
 * `moduleStore` and `experienceStore`.
 */
export async function loadDomainConfig(domain: string): Promise<DomainConfigData> {
  useModuleStore.getState().setConfig(null);
  useExperienceStore.getState().setExperience(null, { loadState: 'loading' });

  try {
    const { data } = await tenantApi.get('/public/domain-config', {
      params: { domain, _ts: Date.now() },
    });
    const config = unwrap<DomainConfigData>(data);

    useModuleStore.getState().setConfig(config);
    useExperienceStore.getState().setExperience(config.appExperience ?? null, {
      loadState: 'ready',
    });

    return config;
  } catch (error) {
    useExperienceStore.getState().setLoadState('error');
    throw error;
  }
}
